import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface SEOHeadProps {
  /** English title (always required) */
  title: string;
  /** English meta description */
  description: string;
  canonical: string;
  ogImage?: string;
  /** Hungarian title — shown when lang=hu */
  titleHu?: string;
  /** Hungarian meta description */
  descriptionHu?: string;
  schema?: object | object[];
}

const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/HhJf9d7FUdg21FASGIf3Wu4fLeK2/social-images/social-1772661172062-Képernyőfotó_2026-03-04_-_22.52.27.webp";

function setMeta(name: string, content: string, property = false) {
  const attr = property ? "property" : "name";
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setPageSchema(schema: object | object[]) {
  const id = "page-schema-ld";
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
}

function removePageSchema() {
  document.getElementById("page-schema-ld")?.remove();
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  titleHu,
  descriptionHu,
  schema,
}: SEOHeadProps) {
  const { i18n } = useTranslation();
  const isHu = i18n.language === "hu";

  const activeTitle = isHu && titleHu ? titleHu : title;
  const activeDesc = isHu && descriptionHu ? descriptionHu : description;

  useEffect(() => {
    // Sync HTML lang attribute so screen readers and search engines know the language
    document.documentElement.lang = isHu ? "hu" : "en";

    document.title = activeTitle;
    setMeta("description", activeDesc);
    setCanonical(canonical);

    // Open Graph
    setMeta("og:title", activeTitle, true);
    setMeta("og:description", activeDesc, true);
    setMeta("og:url", canonical, true);
    setMeta("og:image", ogImage, true);
    setMeta("og:locale", isHu ? "hu_HU" : "en_US", true);

    // Twitter
    setMeta("twitter:title", activeTitle);
    setMeta("twitter:description", activeDesc);
    setMeta("twitter:image", ogImage);

    // Page-level schema
    if (schema) {
      setPageSchema(schema);
    } else {
      removePageSchema();
    }
  }, [activeTitle, activeDesc, canonical, ogImage, isHu, schema]);

  return null;
}
